export const en = {
  common: {
    appName: 'Novix',
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    back: 'Back',
    continue: 'Continue',
    save: 'Save',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy. Select the text manually.',
    download: 'Download',
    done: 'Done',
    language: 'Language',
    next: 'Next',
    // "All" on the button inside an amount field — moves the whole balance.
    max: 'All',
    backHome: 'Back Home',
    seeAll: 'See all',
    quickAmounts: 'Quick amounts',
    noFee: 'No Fee',
    fee: 'Fee: {{amount}}',
    total: 'Total',
    edit: 'Edit',
    delete: 'Delete',
    comingSoon: 'This section arrives in a later phase.'
  },
  nav: {
    primary: 'Primary navigation',
    home: 'Home',
    history: 'History',
    wallet: 'Wallet',
    cards: 'Cards',
    payouts: 'Payouts',
    profile: 'Profile'
  },
  wallet: {
    yourBalance: 'Your balance',
    hideBalance: 'Hide balance',
    showBalance: 'Show balance',
    balanceHidden: 'Balance hidden',
    transactions: 'Transactions',
    addCard: 'Add new',
    totalBalance: 'Total balance',
    balanceNotAvailable: 'Balance is not available yet',
    balanceFailed: 'Could not load the balance',
    actions: {
      deposit: 'Deposit',
      withdraw: 'Withdraw',
      transfer: 'Transfer'
    },
    yourCards: 'Your cards',
    cardsEmptyTitle: 'No cards yet',
    cardsEmptyBody: 'Card issuing arrives in a later phase.',
    recentActivity: 'Recent activity',
    seeAll: 'See all',
    activityScopeNote:
      'This list shows deposits, withdrawals and transfers. Card operations arrive in later phases.'
  },
  activity: {
    title: 'Activity',
    empty: 'No operations yet.',
    unavailable: 'Could not load the activity history.',
    partial: 'Part of the history could not be loaded — this list may be incomplete.',
    toUser: 'To user #{{id}}',
    fromUser: 'From user #{{id}}',
    cardUnknown: 'Card',
    kind: {
      deposit: { in: 'Deposit', out: 'Deposit' },
      withdrawal: { in: 'Crypto withdrawal', out: 'Crypto withdrawal' },
      payout: { in: 'Payout to card', out: 'Payout to card' },
      transfer: { in: 'Incoming transfer', out: 'Outgoing transfer' }
    },
    status: {
      pending: 'In progress',
      completed: 'Completed',
      failed: 'Declined',
      // Deliberately a different word and a different colour from "Declined":
      // the operation did not go through, and the money is back. Two facts.
      reversed: 'Refunded',
      unresolved: 'Being confirmed'
    },
    secondary: {
      deposit: 'Credited {{amount}}',
      withdrawal: 'Debited {{amount}}',
      payout: 'Debited {{amount}}',
      transfer: 'Debited {{amount}}'
    }
  },
  withdraw: {
    methodTitle: 'Withdraw',
    methodSubtitle: 'Choose where to send your funds.',
    methodCrypto: 'Cryptocurrency',
    methodCryptoHint: 'USDT to an external address on TRON or BNB Smart Chain',
    methodCard: 'Bank card',
    methodCardHint: 'Roubles to a card, from ₽5,000',
    methodTransfer: 'Transfer inside Novix',
    methodTransferHint: 'USDT to another user by their ID',

    amountLabel: 'Amount in {{currency}}',
    amountNotANumber: 'Enter the amount as a number',
    max: 'Max',
    confirm: 'Confirm withdrawal',
    stepUpNote: 'You will need a code from your authenticator app to confirm.',
    quoteUnavailableTitle: 'Quote unavailable',
    historyPollNote: 'The status updates automatically.',

    // The review step. Sending money out is irreversible; this is where a
    // pasted-from-the-wrong-tab address and an extra zero get caught.
    review: {
      summaryLabel: 'Review this operation',
      edit: 'Edit details',
      note: 'Check the amount and the recipient. This cannot be undone once confirmed.'
    },
    receipt: {
      summaryLabel: 'Operation details',
      dateTime: 'Date and time',
      txHash: 'Transaction hash',
      operationId: 'Operation ID'
    },

    destination: {
      crypto: 'to the address',
      card: 'to the card'
    },

    submitted: {
      // A 200 is not "sent". The funds are debited and queued; the word "sent"
      // only appears once the backend has confirmed completion.
      pending: {
        title: 'Request accepted',
        body: 'The funds have been debited and queued for sending {{destination}}. Nothing has been sent yet — the status updates automatically.'
      },
      completed: {
        title: 'Sent',
        body: 'The funds have been sent {{destination}}.'
      },
      reversed: {
        title: 'Cancelled and refunded',
        body: 'Sending {{destination}} did not succeed. The full amount has been returned to your balance.'
      },
      unresolved: {
        title: 'Outcome is being confirmed',
        body: 'We do not yet know whether the funds reached {{destination}}. A specialist is checking; nothing is refunded automatically in this state.'
      },
      failed: {
        title: 'Operation declined',
        body: 'Sending {{destination}} did not succeed.'
      },
      reason: 'Reason: {{reason}}',
      startAnother: 'Start another operation'
    },

    crypto: {
      title: 'Crypto withdrawal',
      subtitle: 'Choose the network, the destination address and the amount.',
      chooseNetwork: 'Withdrawal network',
      addressLabel: 'Destination address',
      addressHint: 'An address on {{network}}.',
      addressPlaceholder: {
        trc20: 'T…',
        bsc: '0x…'
      },
      addressInvalid: 'This does not look like a {{network}} address. Please check it.',
      addressOtherNetwork:
        'This looks like an address on another network, not {{network}}. Sending to it would lose the funds permanently.',
      warningTitle: 'Sending on {{network}} only',
      warningBody:
        'The funds will be sent over {{network}}. If the destination address belongs to a different network, the {{currency}} will be lost permanently — the transfer cannot be reversed.',
      recipientGets: 'Recipient gets',
      networkFee: 'Network fee',
      networkFeeHint: 'Charged on top of the amount',
      commission: 'Novix commission',
      totalDebit: 'Debited from your balance',
      minimum: 'Minimum amount',
      maxWithdrawable: 'Available to withdraw',
      maxWithdrawableHint: 'Balance less the fees',
      remainingDailyLimit: 'Remaining daily limit',
      summaryLabel: 'Withdrawal breakdown',
      networkRow: 'Network',
      recipientRow: 'Recipient',
      belowMinimum: 'The minimum withdrawal is {{amount}}',
      aboveMax: 'You can withdraw at most {{amount}}',
      historyTitle: 'Crypto withdrawals'
    },

    card: {
      title: 'Payout to a card',
      subtitle: 'Enter the amount in roubles and the recipient card.',
      amountLabel: 'Amount in roubles',
      minimumHint: 'The minimum payout is {{amount}}.',
      belowMinimum: 'The minimum payout is {{amount}}',
      belowMinimumUnknown: 'That amount is below the minimum for a card payout.',
      recipientGets: 'The card receives',
      rate: 'Rate',
      rateValue: '1 USDT = ₽{{rate}}',
      commission: 'Novix commission',
      usdtDebit: 'Debited from your balance',
      usdtDebitHint: 'Commission included',
      minimum: 'Minimum amount',
      summaryLabel: 'Payout breakdown',
      cardRow: 'Recipient card',
      amountRow: 'Amount',
      numberLabel: 'Recipient card number',
      numberHint: '16 digits from the front of the card.',
      numberInvalid: 'Please check the card number — there is a mistake in it.',
      numberChecked: 'Card number passes the check',
      numberRejected: 'The bank did not accept this card number. Please check it and try again.',
      privacyNote: 'The card number goes to the payment partner only and is never stored here.',
      historyTitle: 'Card payouts'
    }
  },
  transfer: {
    title: 'Transfer inside Novix',
    subtitle: 'Send USDT to another Novix user by their ID.',
    recipientLabel: 'Recipient ID',
    recipientHint: 'The numeric identifier of a Novix user.',
    recipientInvalid: 'The recipient ID is a number.',
    noteLabel: 'Note (optional)',
    noteHint: 'The recipient will see it. Up to 140 characters.',
    recipientGets: 'Recipient gets',
    fee: 'Novix commission',
    feeHint: 'Charged on top of the amount',
    totalDebit: 'Debited from your balance',
    availableBalance: 'Available balance',
    summaryLabel: 'Transfer breakdown',
    recipientRow: 'Recipient',
    reviewNote: 'The recipient will see this transfer in the Novix app.',
    insufficientFunds: 'Your balance does not cover this transfer and its commission.',
    belowMinimum: 'The minimum transfer is {{amount}}',
    confirm: 'Send transfer',
    // A transfer is the one money operation with no external call: a single
    // ledger transaction, so "completed" here is the truth.
    sentTitle: 'Transfer completed',
    sentBody: 'User #{{id}} received {{amount}}.',
    sendAnother: 'Make another transfer',
    historyTitle: 'Transfers'
  },
  deposit: {
    methodTitle: 'Deposit',
    methodSubtitle: 'Choose how you want to top up your account.',
    methodCrypto: 'Cryptocurrency',
    methodCryptoHint: 'USDT on TRON and BNB Smart Chain',
    methodCard: 'Bank card',
    methodInternal: 'Transfer from another user',

    shareId: {
      rowHint: 'Show your ID for transfers',
      transferLink: 'My ID for receiving a transfer',
      heading: 'Your Novix ID',
      lead: 'Send this ID to whoever wants to transfer funds to you — it is how they find your account.',
      scopeNote:
        'This ID works only for transfers between Novix users. It cannot be used to top up from another wallet or a bank.',
      failedTitle: 'Could not load your ID'
    },

    cryptoTitle: 'Crypto deposit',
    cryptoSubtitle: 'Pick a network and send funds to the address below.',
    chooseNetwork: 'Choose a network',
    networks: {
      trc20: 'TRON (TRC20)',
      bsc: 'BNB Smart Chain (BEP-20)',
      unknown: 'Unknown network'
    },
    networkHints: {
      trc20: 'Tron network',
      bsc: 'BNB Smart Chain network'
    },

    addressFor: 'Your {{currency}} address on {{network}}',
    addressLabel: 'Deposit address',
    addressLength: 'Address length is {{length}}. It is shown in full, never shortened.',
    copyAddress: 'Copy address',
    qrLabel: 'QR code of the {{currency}} address on {{network}}',

    warning: {
      title: 'Send on {{network}} only',
      body: 'A transfer sent on any other network is lost permanently and cannot be recovered. Make sure the sending wallet is sending {{currency}} on {{network}}.'
    },

    minimumLabel: 'Minimum deposit',
    minimumHint: 'A transfer smaller than {{amount}} will not be credited.',
    feeFrom: 'from {{amount}}',
    // The method chooser's pill: chosen when the API states a fee of zero, never
    // as a fallback for a fee that could not be read. See DepositMethodPage.
    //
    // Its own key rather than common.noFee (which the address screen's fee row
    // uses) because the pill has a hard width budget: at 390px it sits against
    // "Криптовалюта", which cannot hyphenate, and the longer wording breaks that
    // word in half. Already fixed once — see the comment on FeePill.
    feeNone: 'Free',
    feeLabel: 'Provider fee',
    feeHint:
      'The provider deducts {{fee}} from every {{network}} deposit, whatever the amount. Sending a small amount means most of it goes to the fee.',
    feeFreeHint: 'A {{network}} deposit is credited in full — the provider takes no commission.',
    // The history row's own figure, from the webhook. It exists because a row
    // can carry a fee on a day the screen above says deposits are free.
    rowFee: 'Fee {{amount}}',

    addressFailedTitle: 'Could not get the address',

    historyTitle: 'Deposits',
    historyEmpty: 'No deposits yet.',
    historyUnavailable: 'Could not load the deposit history.',
    historyPollNote: 'The list refreshes automatically. Crediting takes a few minutes.',
    rowTitle: 'Deposit',
    credited: 'Credited {{amount}}',
    openReceipt: 'Open deposit details',
    receipt: {
      summaryLabel: 'Deposit details',
      detailsLabel: 'Transaction',
      amount: 'Amount sent',
      credited: 'Credited to your balance',
      creditedPending: 'Known once it is credited',
      dateTime: 'Date and time',
      txHash: 'Transaction hash',
      operationId: 'Operation ID',
      notFoundTitle: 'Deposit not found',
      notFoundBody: 'This operation is not in your deposit history.'
    },
    status: {
      pending: 'Processing',
      confirmed: 'Credited',
      failed: 'Rejected'
    },
    backHome: 'Back to home'
  },
  auth: {
    login: 'Sign in',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signIn: 'Sign in',
    signOut: 'Sign out',
    totpTitle: 'Verification code',
    totpHint: 'Enter the six-digit code from your authenticator app',

    welcomeTitle: 'Novix — your USDT wallet',
    loginTitle: 'Sign in to your account',
    loginSubtitle: 'Enter your email and password to continue',
    forgotPassword: 'Forgot your password?',
    noAccount: 'No account yet?',
    createAccount: 'Create an account',
    haveAccount: 'Already have an account?',
    backToLogin: 'Back to sign in',

    gate: {
      checking: 'Checking your session…',
      failedTitle: 'Could not check your session',
      // Deliberately does not claim the user is signed out: we do not know.
      failedBody:
        'We cannot reach the server, so we cannot confirm you are signed in. Check your connection and try again — or sign in afresh.'
    },

    sessionEnded: {
      revokedTitle: 'You were signed out for security',
      revoked:
        'The session was terminated: an already-rotated refresh token was replayed. If this was not you, change your password right after signing in.',
      expiredTitle: 'Session ended',
      expired: 'Your session expired. Please sign in again.'
    },

    register: {
      title: 'Create an account',
      step: 'Step {{current}} of {{total}}',
      emailStepTitle: 'Your email',
      emailStepHint: 'We send the confirmation link to this address.',
      passwordStepTitle: 'Choose a password',
      passwordStepHint: 'You will use it to sign in to {{email}}.',
      confirmPassword: 'Repeat password',
      submit: 'Create account',
      changeEmail: 'Change email',
      successTitle: 'Check your inbox',
      // Wording deliberately does not confirm whether the account exists — the
      // backend answers identically for a new and an already-taken address.
      successBody:
        'If this address can be registered, we have sent a confirmation link to {{email}}.',
      successHint: 'The link is valid for a limited time. Check your spam folder.',
      resend: 'Send the email again',
      resendIn: 'You can resend in {{seconds}}s',
      resent: 'Email sent again.',
      devLinkTitle: 'Development mode',
      devLinkBody: 'The backend returned a confirmation token. This never happens in production.',
      devLinkAction: 'Open the confirmation link'
    },

    passwordRules: {
      requirementsTitle: 'Your password must contain:',
      minLength: 'at least {{count}} characters',
      letterAndDigit: 'at least one letter and one digit',
      maxLength: 'at most {{count}} characters',
      required: 'Enter a password',
      tooShort: 'The password must be at least {{count}} characters long',
      tooLong: 'The password must be at most {{count}} characters long',
      needsLetterAndDigit: 'The password must contain at least one letter and one digit',
      mismatch: 'The passwords do not match',
      requirementMet: 'met',
      requirementUnmet: 'not met'
    },

    emailErrors: {
      required: 'Enter your email address',
      invalid: 'Check your email address',
      tooLong: 'The email address is too long'
    },

    confirmEmail: {
      title: 'Email confirmation',
      verifying: 'Checking the link…',
      successTitle: 'Email confirmed',
      successBody: 'Your account is active. You can sign in now.',
      failedTitle: 'This link is not valid',
      failedBody: 'The link has already been used or has expired. Request a new one.',
      missingTokenTitle: 'No confirmation token',
      missingTokenBody:
        'Open the full link from the email — the address is missing the token parameter.',
      goToLogin: 'Go to sign in',
      requestNewLink: 'Request a new link'
    },

    forgot: {
      title: 'Password recovery',
      subtitle: 'Enter your account email and we will send a password reset link.',
      submit: 'Send the link',
      sentTitle: 'Check your inbox',
      // Same principle as registration: the response never reveals whether the
      // account exists.
      sentBody: 'If an account exists for {{email}}, we have sent a password reset link to it.',
      sentHint: 'The link is valid for a limited time. Check your spam folder.'
    },

    reset: {
      title: 'New password',
      subtitle: 'Choose a new password for your account.',
      submit: 'Save password',
      successTitle: 'Password changed',
      successBody: 'Sign in with your new password.',
      missingTokenTitle: 'No reset token',
      missingTokenBody:
        'Open the full link from the email — the address is missing the token parameter.',
      requestNewLink: 'Request a new link'
    },

    twoFactor: {
      challengeTitle: 'Confirm sign-in',
      challengeSubtitle: 'Enter the six-digit code from your authenticator app.',
      verify: 'Confirm',
      expiresIn: 'Enter the code within {{time}}',
      expiredTitle: 'Time is up',
      expiredBody: 'The sign-in challenge is valid for a limited time. Please sign in again.',
      restart: 'Sign in again',
      noSessionTitle: 'No sign-in in progress',
      noSessionBody: 'Start by entering your email and password.',
      useRecovery: 'Use a recovery code',
      useAuthenticator: 'Enter a code from the app',
      recoveryTitle: 'Recovery code',
      recoverySubtitle:
        'Enter one of the recovery codes you saved when you enabled two-factor protection. Each code works once.',
      recoveryLabel: 'Recovery code',
      recoveryPlaceholder: 'xxxx-xxxx',
      recoveryRequired: 'Enter a recovery code',
      errorInvalid: 'Wrong code. Check your authenticator app and try again.',
      errorReplayed: 'This code has already been used. Wait for the next code and enter that one.',
      errorLocked: 'Too many attempts. Wait 15 minutes and try again.',
      errorNotConfigured: 'Two-factor protection is not set up for this account.'
    }
  },
  security2fa: {
    title: 'Two-factor protection',
    subtitle: 'A one-time code from your authenticator app, on top of your password.',
    statusEnabled: 'Enabled',
    statusDisabled: 'Not enabled',
    statusEnabledBody: 'A code from the app is required to sign in and to move money.',
    statusDisabledBody:
      'Your account is protected by a password alone. We recommend adding a second factor.',
    enable: 'Enable',
    startOver: 'Start over',
    setup: {
      title: 'Setup',
      step1Title: 'Step 1. Scan the QR code',
      step1Body:
        'Open your authenticator app (Google Authenticator, 1Password, Aegis) and add an account.',
      qrLabel: 'QR code for your authenticator app',
      cantScan: 'Cannot scan it?',
      manualKeyLabel: 'Manual entry key',
      copyKey: 'Copy the key',
      step2Title: 'Step 2. Confirm the code',
      step2Body: 'Enter the six-digit code shown by the app.',
      submit: 'Enable',
      loadFailed: 'Could not load the setup data.'
    },
    recovery: {
      title: 'Recovery codes',
      body: 'Ten single-use codes in case you lose access to your authenticator app.',
      warningTitle: 'These codes are shown exactly once',
      warningBody:
        'Once you leave this screen you cannot see them again. Save them to a password manager or print them.',
      list: 'List of recovery codes',
      copyAll: 'Copy all',
      download: 'Download as a file',
      filename: 'novix-recovery-codes.txt',
      fileHeader: 'Novix recovery codes. Each code works once. Keep them somewhere safe.',
      savedCheckbox: 'I have saved the recovery codes somewhere safe',
      done: 'Done',
      enabledToast: 'Two-factor protection is enabled.'
    },
    disable: {
      title: 'Disable two-factor protection',
      body: 'Enter the current code from your authenticator app to confirm.',
      warning:
        'After disabling it, your account is protected by a password alone and your saved recovery codes stop working.',
      submit: 'Disable',
      open: 'Disable protection',
      doneToast: 'Two-factor protection is disabled.'
    }
  },
  stepUp: {
    title: 'Confirm this operation',
    description: 'Enter the current code from your authenticator app.',
    submit: 'Confirm',
    reason: {
      money: 'Moving money requires confirmation with a code.',
      'card-sensitive': 'Revealing card details requires confirmation with a code.',
      security: 'Changing security settings requires confirmation with a code.'
    }
  },
  otp: {
    label: 'Verification code',
    digit: 'Digit {{index}} of {{total}}',
    resendIn: 'Resend in {{seconds}}s',
    resendPrompt: 'No code?',
    resend: 'Send again',
    resending: 'Sending…'
  },
  // The transfer contact book. A contact is a private nickname over an id: the
  // server deliberately will not say whether a user id exists, so nothing below
  // promises a check that never happened.
  contacts: {
    title: 'Contacts',
    subtitle: 'Your own names for the ids you send to.',
    add: 'Add contact',
    idLine: 'ID {{id}}',
    editAction: 'Rename contact “{{label}}”',
    deleteAction: 'Delete contact “{{label}}”',
    emptyTitle: 'No contacts yet',
    emptyBody:
      'A contact is your own name for a Novix user id. Save an id once and you will not have to type it again when you send.',
    emptyPrivacy:
      'Only you can see your contacts. Novix does not check the id and does not tell anyone you saved them.',
    loadFailedTitle: 'Could not load your contacts',
    goToTransfer: 'Go to transfer',
    added: 'Contact “{{label}}” added',
    renamed: 'Contact renamed to “{{label}}”',
    deleted: 'Contact “{{label}}” deleted',
    deleteTitle: 'Delete this contact?',
    deleteBody: 'The contact “{{label}}” will be removed from your book. This cannot be undone.',
    deleteKeeps:
      'Past transfers stay in your history — only the entry in your contact book is removed.',
    pickerTitle: 'Choose a contact',
    pickerHint: 'The transfer goes to the id saved in this contact.',
    manage: 'Manage contacts',
    chooseFromBook: 'Choose from contacts',
    clearChoice: 'Clear',
    form: {
      addTitle: 'New contact',
      addHint:
        'Novix does not check whether this user exists and does not notify them. Please check the id yourself.',
      renameTitle: 'Rename contact',
      renameHint: 'Only the name changes. The recipient id stays the same.',
      idLabel: 'Novix user id',
      idHint: 'The numeric id the recipient sees on their own profile.',
      idInvalid: 'An id is a number.',
      idFixed: 'ID {{id}} — cannot be changed',
      labelLabel: 'What to call them',
      labelHint: 'Only you see this. Up to {{max}} characters.',
      labelTooLong: 'That name is longer than {{max}} characters.',
      addSubmit: 'Add'
    }
  },

  // The saved-card book for rouble payouts. No endpoint returns a full card
  // number — every screen shows the server's mask and nothing else.
  cards: {
    title: 'Saved cards',
    subtitle: 'Cards for rouble payouts, without retyping the number.',
    add: 'Add card',
    emptyTitle: 'No saved cards yet',
    emptyBody:
      'Save a card once and you can pick it from a list when you withdraw roubles, instead of typing the number.',
    emptyPrivacy:
      'The number is stored encrypted and is never shown in full — not here and not in your history.',
    loadFailedTitle: 'Could not load your cards',
    goToPayout: 'Go to card withdrawal',
    added: 'Card {{mask}} saved',
    edited: 'Card {{mask}} updated',
    deleted: 'Card {{mask}} deleted',
    deleteTitle: 'Delete this card?',
    deleteBody: 'Card {{mask}} will be removed from your saved cards. This cannot be undone.',
    deleteKeeps:
      'Past payouts stay in your history — only the saved card is removed, not the payments made to it.',
    pickerTitle: 'Choose a card',
    pickerHint: 'The money goes to the card you choose.',
    manage: 'Manage cards',
    editAction: 'Edit card {{mask}}',
    deleteAction: 'Delete card {{mask}}',
    chooseFromBook: 'Choose a saved card',
    clearChoice: 'Clear',
    unreadableInstruction:
      'This card can no longer be read. Delete it and save it again — it cannot be paid to right now.',
    form: {
      addTitle: 'New card',
      addHint: 'The card number is sent once and never comes back.',
      editTitle: 'Edit card',
      editHint: 'The card number cannot be changed. To use a different one, save a new card.',
      numberLabel: 'Card number',
      labelLabel: 'Card name',
      labelPlaceholder: 'For example, “Salary”',
      labelHint: 'Only you see this. Up to {{max}} characters.',
      labelRequired: 'Give the card a name — it is how you recognise it in the list.',
      labelTooLong: 'That name is longer than {{max}} characters.',
      addSubmit: 'Save card',
      privacyNote:
        'We store the number encrypted and show only the first six and last four digits.',
      rateLimited: 'Too many attempts. Wait a moment and try saving the card again.'
    }
  },

  profile: {
    title: 'Profile',
    idLabel: 'ID {{id}}',
    copyId: 'Copy ID',
    copyIdValue: 'Copy ID {{id}}',
    idUnavailable: 'ID is not available yet',
    contactSupport: 'Contact\nThe Support',
    contactSupportPlain: 'Contact the support',
    supportUnavailable: 'The support channel is not connected yet.',
    sectionProfile: 'Profile',
    sectionSettings: 'Settings',
    accountSafety: 'Account safety',
    contacts: 'Contacts',
    contactsHint: 'Your names for the ids you send to',
    savedCards: 'Saved cards',
    savedCardsHint: 'Cards for rouble payouts',
    emailRow: 'Email',
    emailUnknown: 'Not set',
    passwordRow: 'Password',
    twoFactorRow: 'Two-factor authentication',
    signOut: 'Sign Out',
    signOutConfirmTitle: 'Sign Out',
    signOutConfirmBody:
      'Are you sure you want to sign out? You’ll need to enter your credentials again the next time you sign in.',
    stayLoggedIn: 'Stay Logged In',
    signedOut: 'You have signed out.'
  },
  theme: {
    label: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    toggle: 'Toggle theme'
  },
  errors: {
    title: 'Something went wrong',
    description: 'This section could not be displayed. Try reloading the page.',
    notFound: 'Page not found',
    notFoundDescription: 'This page does not exist or has been moved.',
    goHome: 'Go home',
    unknown: 'Unexpected error. Please try again.',
    kind: {
      http: 'The service is temporarily unavailable. Please try again later.',
      network: 'Cannot reach the server. Check your connection.',
      timeout: 'The server did not respond in time. Try again.',
      'rate-limit': 'Too many requests. Please wait {{seconds}}s.',
      unauthorized: 'Your session expired. Please sign in again.',
      parse: 'The server returned an unexpected response.'
    },
    code: {
      // Keys are the backend envelope's errorCode, verbatim. Parsing the message
      // text is forbidden: it is unstable and untranslated.
      invalid_credentials: 'Incorrect email or password.',
      account_locked: 'Too many attempts. Wait 15 minutes and try again.',
      account_blocked: 'This account is blocked. Contact support.',
      invalid_token: 'The link is invalid or has expired.',
      invalid_refresh_token: 'Your session expired. Please sign in again.',
      refresh_token_reuse: 'The session was terminated for security. Please sign in again.',
      invalid_2fa_code: 'Incorrect verification code.',
      '2fa_code_already_used': 'This code has already been used. Wait for the next one.',
      '2fa_not_configured': 'Two-factor protection is not set up for this account.',
      '2fa_already_enabled': 'Two-factor protection is already enabled.',
      '2fa_unavailable': 'Two-factor protection is temporarily unavailable. Contact support.',
      step_up_required: 'A code from your authenticator app is required.',
      unauthenticated: 'You need to sign in.',
      validation_failed: 'Check the fields and try again.',
      INSUFFICIENT_FUNDS: 'Insufficient funds.',

      // Crypto withdrawal
      crypto_unsupported_network: 'That network is not supported.',
      crypto_invalid_address: 'That address does not match the selected network. Please check it.',
      crypto_address_not_activated:
        'That TRON address has never been used and cannot receive USDT yet. Send it a small amount of TRX first.',
      crypto_withdrawal_below_minimum: 'That amount is below the minimum for this network.',
      crypto_withdrawal_insufficient_funds:
        'Your balance does not cover this amount together with the network fee.',
      crypto_withdrawal_daily_limit_exceeded:
        'This would exceed your daily withdrawal limit. Please try again tomorrow.',
      crypto_withdrawal_temporarily_unavailable:
        'Withdrawals are temporarily unavailable. Please try again shortly.',
      crypto_provider_unavailable:
        'The service is temporarily unavailable. Please try again shortly.',
      crypto_idempotency_key_reused: 'That operation has already been submitted.',

      // Rouble payout to a card
      payout_below_minimum: 'That amount is below the minimum for a card payout.',
      payout_invalid_card_number: 'Please check the card number.',
      payout_insufficient_funds: 'Your balance does not cover this payout.',
      payout_provider_limit: 'That amount is not available right now. Please try a different one.',
      payout_quote_unavailable: 'Payouts are temporarily unavailable. Please try again shortly.',
      payout_idempotency_key_reused: 'That operation has already been submitted.',

      // Transfers
      transfer_below_minimum: 'That amount is below the minimum transfer.',
      transfer_self: 'You cannot transfer to yourself.',
      transfer_recipient_unavailable: 'That recipient cannot receive transfers.',
      transfer_insufficient_funds: 'Your balance does not cover this transfer.',
      transfer_idempotency_key_reused: 'That transfer has already been submitted.',

      // The contact book. All three are safe to show verbatim: they are answers
      // about the caller's own book, not about anybody else's account.
      transfer_contact_not_found: 'That contact is not in your book.',
      transfer_contact_label_invalid: 'Give the contact a name, up to 64 characters.',
      transfer_contact_self: 'You cannot add yourself as a contact.',
      transfer_contact_already_exists: 'That user is already in your contacts.',
      // A client bug rather than a user error: both addressing fields were sent.
      transfer_recipient_ambiguous: 'Give either a recipient id or a contact, not both.',
      transfer_recipient_required: 'Choose who to pay.',

      // Saved cards
      payout_saved_card_not_found: 'No such saved card.',
      payout_saved_card_already_exists: 'That card is already saved — it is in your list.',
      payout_saved_card_unreadable:
        'That saved card can no longer be read. Please remove it and save it again.',
      payout_invalid_card_label: 'A card needs a name of at most 64 characters.',
      payout_card_ambiguous: 'Give either a card number or a saved card, not both.',
      payout_card_required: 'Choose a card to pay to.'
    }
  }
} as const;
